import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Star, Plus, Trash2, Loader2, ArrowUp, ArrowDown, Quote } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal, Field, inputCls } from "./common";

const empty = { name: "", location: "", text: "", rating: 5, date: "" };

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange?.(n)} data-testid={`star-${n}`}>
          <Star className={`w-6 h-6 ${n <= value ? "fill-gold text-gold" : "text-charcoal/25"}`} />
        </button>
      ))}
    </div>
  );
}

export default function TestimonialsManager() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/testimonials/manage"); setItems(data); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.put(`/testimonials/${id}`, { status: "approved" }); toast.success("Approved — now live on the homepage"); load(); }
    catch { toast.error("Failed to approve"); }
  };

  const set = (k, v) => setModal((m) => ({ ...m, [k]: v }));
  const save = async () => {
    try {
      if (modal.id) await api.put(`/testimonials/${modal.id}`, modal);
      else await api.post("/testimonials", modal);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/testimonials/${id}`); toast.success("Deleted"); setConfirmDel(null); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const move = async (idx, dir) => {
    const arr = [...items]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; setItems(arr);
    try { await api.post("/testimonials/reorder", { order: arr.map((t) => t.id) }); } catch { load(); }
  };

  const publicIds = new Set((items || []).filter((t) => t.status !== "pending").slice(0, 6).map((t) => t.id));

  return (
    <div data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-600 text-charcoal flex items-center gap-2"><Quote className="w-6 h-6 text-emerald" /> Testimonials</h2>
        <button onClick={() => setModal({ ...empty })} data-testid="new-testimonial-btn" className="inline-flex items-center gap-2 bg-emerald text-white px-4 py-2.5 rounded-sm text-sm font-600 hover:bg-emerald-dark"><Plus className="w-4 h-4" /> Add Review</button>
      </div>
      <p className="text-sm text-olive mb-5 -mt-1" data-testid="testimonials-display-hint">Only the first <strong>6 approved</strong> reviews show on your homepage. Use the arrows to reorder which ones appear.</p>
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-olive" /> : items.length === 0 ? (
        <p className="text-olive" data-testid="testimonials-empty">No testimonials yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((t, idx) => (
            <div key={t.id} className={`bg-white border rounded-sm p-5 flex items-start justify-between gap-3 ${t.status === "pending" ? "border-gold ring-1 ring-gold/40" : "border-border"}`} data-testid={`testimonial-${t.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}
                  {t.status === "pending" && <span className="text-[11px] font-700 uppercase tracking-wide bg-gold/20 text-charcoal px-2 py-0.5 rounded-sm" data-testid={`testimonial-pending-${t.id}`}>Pending</span>}
                  {t.status !== "pending" && !publicIds.has(t.id) && <span className="text-[11px] font-600 uppercase tracking-wide bg-charcoal/5 text-charcoal/50 px-2 py-0.5 rounded-sm" data-testid={`testimonial-hidden-${t.id}`}>Not on homepage</span>}
                </div>
                <p className="text-charcoal mt-2 italic">"{t.text}"</p>
                <p className="text-olive text-sm mt-1">— {t.name}, {t.location}{t.date ? ` · ${t.date}` : ""}</p>
              </div>
              <div className="flex flex-col gap-1">
                {t.status === "pending" && (
                  <button onClick={() => approve(t.id)} data-testid={`testimonial-approve-${t.id}`} className="px-2 py-1.5 rounded-sm bg-emerald text-white text-xs font-700 hover:bg-emerald-dark">Approve</button>
                )}
                <button onClick={() => move(idx, -1)} className="p-1.5 rounded-sm text-charcoal/60 hover:bg-border" data-testid={`testimonial-up-${t.id}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} className="p-1.5 rounded-sm text-charcoal/60 hover:bg-border" data-testid={`testimonial-down-${t.id}`}><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => setModal(t)} data-testid={`testimonial-edit-${t.id}`} className="p-1.5 rounded-sm text-emerald hover:bg-emerald/10 text-xs font-600">Edit</button>
                <button onClick={() => setConfirmDel(t)} data-testid={`testimonial-delete-${t.id}`} className="p-1.5 rounded-sm text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title={modal.id ? "Edit Testimonial" : "Add Testimonial"} onClose={() => setModal(null)} testid="testimonial-modal">
          <Field label="Customer Name"><input className={inputCls} data-testid="testimonial-name" value={modal.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Location (city only)"><input className={inputCls} data-testid="testimonial-location" value={modal.location} onChange={(e) => set("location", e.target.value)} placeholder="Rincon, GA" /></Field>
          <Field label="Review"><textarea rows={3} className={inputCls} data-testid="testimonial-text" value={modal.text} onChange={(e) => set("text", e.target.value)} /></Field>
          <Field label="Rating"><Stars value={modal.rating} onChange={(n) => set("rating", n)} /></Field>
          <Field label="Date (optional)"><input type="date" className={inputCls} value={modal.date} onChange={(e) => set("date", e.target.value)} /></Field>
          <button onClick={save} data-testid="testimonial-save-btn" className="bg-emerald text-white px-5 py-2.5 rounded-sm font-600 hover:bg-emerald-dark">Save</button>
        </Modal>
      )}
      {confirmDel && (
        <Modal title="Delete this review?" onClose={() => setConfirmDel(null)} testid="testimonial-delete-modal">
          <p className="text-olive mb-2">This will permanently remove the review from <strong>{confirmDel.name || "this customer"}</strong>.</p>
          <p className="text-charcoal italic mb-5">"{confirmDel.text}"</p>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setConfirmDel(null)} data-testid="testimonial-cancel-delete-btn" className="px-4 py-2.5 rounded-sm text-charcoal/70 font-600 bg-[#F8F9FA] hover:bg-border">Cancel</button>
            <button onClick={() => del(confirmDel.id)} data-testid="testimonial-confirm-delete-btn" className="px-4 py-2.5 rounded-sm bg-red-600 text-white font-600 hover:bg-red-700">Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
